/**
 * 法定ページの表示（R0 §2.1）。
 *
 * **なぜ画面が要るか。** API（`GET /api/v1/public/legal/:slug`）が文面を返せる
 * ようになっても、人間が読める URL が無ければ特商法 11 条の「広告表示」を
 * 満たさない。LP のフッタが張っているリンク（利用規約・プライバシーポリシー・
 * 特商法）も、飛び先が無ければ 404 のままである。
 *
 * **認証不要**。`rootRoute` の直下に置き、`appRoute`（認証ガード付き）の
 * 下には**絶対に置かない**。規約はログインできない人こそ読む必要がある。
 *
 * **未設定なら公開しない。** 事業者情報（`LEGAL_*`）が欠けている文書は
 * サーバが `published:false` と欠落項目名を返す。画面はそれをそのまま
 * 「準備中」として出す。**架空の商号や住所で穴埋めしない。**
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Alert, AlertDescription, AlertTitle, Card, CardContent } from '@mail-sv/ui';
import { FileText, ChevronLeft } from 'lucide-react';
import {
  LEGAL_DOCUMENT_SLUGS,
  type LegalBlock,
  type LegalDocumentSlug,
} from '@mail-sv/shared';
import { publicApi } from '../lib/api.js';
import { env } from '../env.js';

/** `GET /api/v1/public/legal`（一覧）。 */
interface LegalIndexResponse {
  draft: boolean;
  items: {
    slug: LegalDocumentSlug;
    title: string;
    version: string;
    /** この版の効力発生日（`yyyy-MM-dd`）。 */
    effectiveFrom: string;
    /** 効力発生日が未到来のあいだ、既存の契約者に適用され続ける版。 */
    currentVersion: string;
    published: boolean;
    missing: string[];
  }[];
}

/** `GET /api/v1/public/legal/:slug`（本文）。 */
interface LegalDocumentResponse {
  slug: LegalDocumentSlug;
  title: string;
  version: string;
  /** この版の効力発生日（`yyyy-MM-dd`）。 */
  effectiveFrom: string;
  /** 効力発生日が未到来のあいだ、既存の契約者に適用され続ける版。 */
  currentVersion: string;
  published: boolean;
  draft: boolean;
  blocks: LegalBlock[];
  hash: string | null;
  missing: string[];
}

function isLegalSlug(value: string): value is LegalDocumentSlug {
  return (LEGAL_DOCUMENT_SLUGS as readonly string[]).includes(value);
}

/* ------------------------------------------------------------------ 外枠 */

function LegalShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto w-full max-w-3xl space-y-6">
        <div className="flex items-center justify-between gap-4">
          <Link to="/legal" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <FileText className="size-4" aria-hidden />
            {env.brandName} 規約・法令表示
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
            ログイン
          </Link>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ 一覧 */

export function LegalIndexPage() {
  const query = useQuery({
    queryKey: ['public', 'legal'],
    queryFn: () => publicApi.get<LegalIndexResponse>('/legal'),
    staleTime: 5 * 60 * 1000,
  });

  return (
    <LegalShell>
      <h1 className="text-2xl font-semibold">規約・法令表示</h1>
      {query.isError ? (
        <Alert variant="destructive">
          <AlertTitle>読み込めませんでした</AlertTitle>
          <AlertDescription>時間をおいて再度お試しください。</AlertDescription>
        </Alert>
      ) : null}
      <div className="space-y-3">
        {(query.data?.items ?? []).map((item) => (
          <Card key={item.slug}>
            <CardContent className="flex items-center justify-between gap-4 py-4">
              <div className="space-y-1">
                <Link
                  to="/legal/$slug"
                  params={{ slug: item.slug }}
                  className="font-medium hover:underline"
                >
                  {item.title}
                </Link>
                <p className="text-xs text-muted-foreground">
                  版 {item.version}
                  {item.published ? '' : '（準備中）'}
                </p>
                {/*
                  **一覧の段階で「改定予定」を出す。**

                  利用規約 第 11 条 2 項は「契約者に不利益となる変更は効力発生日の
                  30 日前までに通知する」と掲示している。本文ページには適用開始日の
                  告知を出していたが、一覧にはどこにも出ておらず、**改定が近い文書が
                  どれなのかは 7 本を 1 本ずつ開かないと分からなかった**。
                  掲示した手続を製品の側で履行するための導線なので、
                  サーバが返している `currentVersion` をここでも使う。
                */}
                {item.published && item.currentVersion && item.currentVersion !== item.version ? (
                  <p className="text-xs font-medium text-foreground">
                    {item.effectiveFrom} から改定（それまでは {item.currentVersion} 版が適用されます）
                  </p>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </LegalShell>
  );
}

/* ------------------------------------------------------------------ 本文 */

/** ブロック 1 つを描く。**HTML は差し込まない**（文面は必ずテキストとして出す）。 */
function Block({ block }: { block: LegalBlock }) {
  switch (block.kind) {
    case 'section':
      return (
        <h2 className="pt-4 text-base font-semibold">
          {block.number} {block.heading}
        </h2>
      );
    case 'paragraph':
      return <p className="whitespace-pre-wrap leading-relaxed text-muted-foreground">{block.text}</p>;
    case 'list':
      return block.ordered ? (
        <ol className="list-decimal space-y-1 pl-6 text-muted-foreground">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ol>
      ) : (
        <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
          {block.items.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  <th scope="row" className="w-1/3 py-2 pr-4 text-left align-top font-medium">
                    {row.label}
                  </th>
                  <td className="whitespace-pre-wrap py-2 text-muted-foreground">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'notice':
      return (
        <Alert variant={block.tone === 'warning' ? 'warning' : 'info'}>
          <AlertDescription>{block.text}</AlertDescription>
        </Alert>
      );
    default:
      // 将来ブロック種別が増えたとき、黙って空白にせず存在だけは見せる。
      return null;
  }
}

export function LegalDocumentPage({ slug }: { slug: string }) {
  const valid = isLegalSlug(slug);
  const query = useQuery({
    queryKey: ['public', 'legal', slug],
    queryFn: () => publicApi.get<LegalDocumentResponse>(`/legal/${slug}`),
    enabled: valid,
    staleTime: 5 * 60 * 1000,
  });

  if (!valid) {
    return (
      <LegalShell>
        <Alert variant="destructive">
          <AlertTitle>ページが見つかりません</AlertTitle>
          <AlertDescription>
            指定された文書は存在しません。<Link to="/legal" className="underline">一覧へ戻る</Link>
          </AlertDescription>
        </Alert>
      </LegalShell>
    );
  }

  const doc = query.data;
  return (
    <LegalShell>
      <Link to="/legal" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="size-4" aria-hidden />
        一覧へ戻る
      </Link>

      {query.isPending ? <p className="text-sm text-muted-foreground">読み込み中…</p> : null}
      {query.isError ? (
        <Alert variant="destructive">
          <AlertTitle>読み込めませんでした</AlertTitle>
          <AlertDescription>時間をおいて再度お試しください。</AlertDescription>
        </Alert>
      ) : null}

      {doc ? (
        <article className="space-y-4">
          <header className="space-y-1">
            <h1 className="text-2xl font-semibold">{doc.title}</h1>
            <p className="text-xs text-muted-foreground">
              版 {doc.version}
              {doc.effectiveFrom ? ` / 適用開始 ${doc.effectiveFrom}` : ''}
              {doc.hash ? ` / 文面ハッシュ ${doc.hash.slice(0, 16)}…` : ''}
            </p>
          </header>

          {/*
            **効力発生日がまだ来ていない版であることを本文の手前に出す。**

            利用規約 第 11 条 2 項は「契約者に不利益となる変更は効力発生日の
            30 日前までに通知する」と掲示している。掲示を差し替えただけで
            効力発生日を示さないと、既存の契約者は「いつからこの内容になるのか」
            「それまではどの版が適用されるのか」を判断できない。
            サーバは `currentVersion`（効力発生日まで適用され続ける版）を
            返しているので、それをそのまま出す。
          */}
          {doc.published && doc.currentVersion && doc.currentVersion !== doc.version ? (
            <Alert variant="info">
              <AlertTitle>この版は {doc.effectiveFrom} から適用されます</AlertTitle>
              <AlertDescription>
                現在ご契約中のお客様には、{doc.effectiveFrom} まで {doc.currentVersion} 版が
                適用されます。変更後の内容に同意されない場合は、適用開始日の前日までに
                解約をお申し出いただけます。
              </AlertDescription>
            </Alert>
          ) : null}

          {doc.published ? (
            doc.blocks.map((block, i) => <Block key={i} block={block} />)
          ) : (
            <Alert variant="warning">
              <AlertTitle>この文書は準備中です</AlertTitle>
              <AlertDescription>
                <p>
                  掲示に必要な事業者情報が未設定のため、この文書はまだ公開されていません。
                  内容が確定するまで、当社はこの文書を根拠とする契約・請求を行いません。
                </p>
                {doc.missing.length > 0 ? (
                  <p className="mt-2 font-mono text-xs">未設定: {doc.missing.join(', ')}</p>
                ) : null}
              </AlertDescription>
            </Alert>
          )}
        </article>
      ) : null}
    </LegalShell>
  );
}
